import { useEffect, useRef, useMemo } from "react";
import { TripperData } from "../types";
import {
  useTripperScrollContext,
  useTripperWidgetContext,
} from "../context/TripperWidgetContext";
import {
  buildBoundsFromCoordinates,
  fitMapToBounds,
  getBoundsArray,
  getStopsWithCoordinates,
} from "../utils/mapHelpers";
import { buildGroupTypeById } from "../utils/groupHelpers";
import {
  isStopVisibleForBusinessTypeFilter,
  selectionToKey,
} from "../utils/businessTypeFilters";
import {
  deriveCameraIntent,
  type Coordinates,
  type CameraRefState,
} from "../utils/cameraIntent";

export const useAutoFollow = ({ data }: { data: TripperData }) => {
  const { activeItemId, activeStopGroupId, isMetadataInView } =
    useTripperScrollContext();
  const { isAutoFollowActive, mapRef, mapInstanceVersion, atlasMode, businessTypeFilterSelection, businessTypeFiltersEnabled } =
    useTripperWidgetContext();

  const refState = useRef<CameraRefState>({
    prevStopCoords: null,
    prevGroupId: null,
    hasInitialAnimated: false,
  });

  const stopsWithCoordinates = useMemo(() => {
    const allStops = getStopsWithCoordinates(data.segments, data.operatorByEntityId);
    if (!businessTypeFiltersEnabled) return allStops;
    return allStops.filter(stop =>
      isStopVisibleForBusinessTypeFilter(
        stop.item,
        data.operatorByEntityId,
        businessTypeFilterSelection,
      ),
    );
  }, [data.segments, data.operatorByEntityId, businessTypeFilterSelection, businessTypeFiltersEnabled]);

  const groupTypeById = useMemo(
    () => buildGroupTypeById(data.segments),
    [data.segments],
  );

  const allStopsBounds = useMemo(
    () => buildBoundsFromCoordinates(stopsWithCoordinates.map(s => s.coordinates)),
    [stopsWithCoordinates],
  );

  // Toggle OFF: zoom out to full overview
  useEffect(() => {
    if (atlasMode || isAutoFollowActive) return;
    fitMapToBounds(mapRef.current, allStopsBounds, {
      animated: true,
      padding: 50,
    });
    refState.current = {
      ...refState.current,
      prevStopCoords: null,
      prevGroupId: null,
    };
  }, [atlasMode, isAutoFollowActive, allStopsBounds, mapRef, mapInstanceVersion, selectionToKey(businessTypeFilterSelection)]);

  // Main camera follow
  useEffect(() => {
    if (atlasMode) return;

    const map = mapRef.current;
    if (!map) return;

    const { intent, nextState } = deriveCameraIntent({
      isAutoFollowActive,
      isMetadataInView,
      activeItemId,
      activeStopGroupId,
      hasBounds: allStopsBounds !== null,
      stopsWithCoordinates,
      groupTypeById,
      segments: data.segments,
      state: refState.current,
    });

    refState.current = nextState;

    switch (intent.type) {
      case "none":
      case "hold":
        break;

      case "overview":
        // TripperMap owns silent overview refits while the hero is in view.
        if (!intent.animated) break;
        fitMapToBounds(map, allStopsBounds, {
          animated: true,
          padding: 64,
        });
        break;

      case "group-entry": {
        const groupBounds = buildBoundsFromCoordinates(
          intent.allCoords as Coordinates[],
        );
        if (groupBounds) {
          map.fitBounds(getBoundsArray(groupBounds), {
            padding: { top: 80, bottom: 80, left: 60, right: 60 },
            duration: !refState.current.prevStopCoords ? 2000 : 2500,
            pitch: 40,
            bearing: 5,
            maxZoom: 16,
          });
        }
        break;
      }

      case "fly-to-stop":
        map.flyTo({
          center: intent.coords,
          zoom: intent.zoom,
          pitch: intent.pitch,
          bearing: intent.bearing,
          duration: intent.duration,
          essential: true,
          curve: 1.4,
        });
        break;

      case "warn-missing-coords":
        console.warn(
          `[Tripper] Stop "${intent.stopId}" is missing coordinates — operator may not be loaded or geocoded.`,
          {
            stopsWithCoords: stopsWithCoordinates.length,
            totalStops: data.segments.length,
          },
        );
        break;
    }
  }, [
    activeItemId,
    activeStopGroupId,
    allStopsBounds,
    atlasMode,
    data.segments,
    groupTypeById,
    isAutoFollowActive,
    isMetadataInView,
    mapInstanceVersion,
    mapRef,
    stopsWithCoordinates,
    selectionToKey(businessTypeFilterSelection),
  ]);
};
